import React, { useState } from "react";
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { type FilterOption } from "./FilterModal";
import PickerModal from "./PickerModal";

import {
  type AttributeMap,
  type AttributeValue,
} from "@/schemas/attribute/AttributeSet";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import {
  type FilterByRange,
  FilterRangeType,
} from "@/schemas/screen/filters/FilterByRange";

type FilterCardProps<T> = {
  range: FilterByRange<T> | null;
  setFilters: (
    newRange: FilterByRange<T>,
    attribute: UserAttributeType,
  ) => void;
  option: FilterOption;
};

function getRangeLabel<T>(
  range: FilterByRange<T> | null,
  table: AttributeMap,
): string {
  if (range === null || (!range.minValueId && !range.maxValueId)) {
    return "Not specified";
  } else if (range.minValueId && !range.maxValueId) {
    return `From ${table[range.minValueId as string]?.label ?? "Unknown"}`;
  } else if (!range.minValueId && range.maxValueId) {
    return `Up to ${table[range.maxValueId as string]?.label ?? "Unknown"}`;
  } else if (range.minValueId && range.maxValueId) {
    return `From ${table[range.minValueId as string]?.label ?? "Unknown"} to ${table[range.maxValueId as string]?.label ?? "Unknown"}`;
  } else {
    return "";
  }
}

function isValidRange<T>(range: FilterByRange<T>): boolean {
  if (range.minValueId && range.maxValueId) {
    return range.minValueId <= range.maxValueId;
  } else {
    return true;
  }
}

function FilterCard<T>({
  range,
  setFilters,
  option,
}: FilterCardProps<T>): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [pickerModalVisible, setPickerModalVisible] = useState(false);
  const [rangeType, setRangeType] = useState<FilterRangeType>(
    FilterRangeType.MIN,
  );

  const resetFilter = (): void => {
    setFilters({ minValueId: null, maxValueId: null }, option.attribute.id);
  };

  const openPickerModal = (rangeType: FilterRangeType): void => {
    setRangeType(rangeType);
    setPickerModalVisible(true);
  };

  const updateRangeValue = (
    rangeType: FilterRangeType,
    value: AttributeValue,
  ): void => {
    let newRange;
    switch (rangeType) {
      case FilterRangeType.MIN:
        newRange = { ...range, minValueId: value.id };
        break;
      case FilterRangeType.MAX:
        newRange = { ...range, maxValueId: value.id };
        break;
    }
    if (isValidRange(newRange as FilterByRange<T>)) {
      setFilters(newRange as FilterByRange<T>, option.attribute.id);
      setPickerModalVisible(false);
    } else {
      Alert.alert("Invalid Range", "Please select a valid range.", [
        { text: "OK" },
      ]);
    }
  };

  return (
    <View key={option.label} style={styles.filterSection}>
      <TouchableOpacity
        style={styles.filterHeader}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <View style={styles.filterIconContainer}>
          <Text style={styles.icon}>{option.icon}</Text>
        </View>
        <View style={styles.filterLabelContainer}>
          <Text style={styles.filterLabel}>{option.label}</Text>
          <Text
            style={styles.filterValue}
          >{`${getRangeLabel(range, option.attribute.map)}`}</Text>
        </View>
        <Ionicons
          name={isExpanded ? "chevron-up" : "chevron-down"}
          size={24}
          color="#6B7280"
        />
      </TouchableOpacity>

      {isExpanded && (
        <View style={styles.filterContent}>
          <View style={styles.rangeContainer}>
            <TouchableOpacity
              style={styles.rangeButton}
              onPress={() => openPickerModal(FilterRangeType.MIN)}
            >
              <Text style={styles.rangeButtonLabel}>Min</Text>
              {range && range.minValueId ? (
                <Text
                  style={styles.rangeButtonValue}
                >{`${option.attribute.map[range.minValueId as string]?.label ?? "Unknown"}`}</Text>
              ) : (
                <></>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.rangeButton}
              onPress={() => openPickerModal(FilterRangeType.MAX)}
              accessibilityLabel="Max Value"
              accessibilityRole="button"
            >
              <Text style={styles.rangeButtonLabel}>Max</Text>
              {range && range.maxValueId ? (
                <Text
                  style={styles.rangeButtonValue}
                >{`${option.attribute.map[range.maxValueId as string]?.label ?? "Unknown"}`}</Text>
              ) : (
                <></>
              )}
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={styles.resetButton}
            onPress={() => resetFilter()}
          >
            <Ionicons name="refresh" size={16} color="#4F46E5" />
            <Text style={styles.resetButtonText}>Reset</Text>
          </TouchableOpacity>
        </View>
      )}
      <PickerModal
        rangeType={rangeType}
        pickerModalVisible={pickerModalVisible}
        updateRangeValue={updateRangeValue}
        setPickerModalVisible={setPickerModalVisible}
        attribute={option.attribute}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  filterSection: {
    marginBottom: 15,
  },
  filterHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    padding: 15,
  },
  filterContent: {
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    padding: 15,
    marginTop: 10,
  },
  rangeContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  filterIconContainer: {
    backgroundColor: "#E5E7EB",
    borderRadius: 20,
    padding: 8,
  },
  filterLabelContainer: {
    flex: 1,
    marginLeft: 15,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: "bold",
  },
  filterValue: {
    fontSize: 14,
    color: "#6B7280",
  },
  rangeButton: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 13,
    marginHorizontal: 5,
  },
  rangeButtonLabel: {
    fontSize: 14,
    color: "#6B7280",
  },
  rangeButtonValue: {
    fontSize: 16,
    fontWeight: "bold",
  },
  resetButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 13,
  },
  resetButtonText: {
    color: "#4F46E5",
    marginLeft: 5,
  },
  icon: {
    fontSize: 30,
    fontWeight: "bold",
  },
});

export default FilterCard;
